import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { AlertCircle, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/utils/toast";
import { useTenantMembers } from "@/hooks/useTenantMembers";
import { EmailChipInput } from "./EmailChipInput";
import { CollaboratorList } from "./CollaboratorList";
import { RemoveCollaboratorConfirmation } from "./RemoveCollaboratorConfirmation";
import type { EmailChip, Collaborator } from "./types";

interface ShareModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  jobId: string;
  collaborators: Collaborator[];
  ownerEmail: string;
  currentUserEmail: string;
  onAddCollaborators: (emails: string[]) => Promise<void>;
  onRemoveCollaborator: (email: string) => Promise<void>;
}

export function ShareModal({
  open,
  onOpenChange,
  title,
  jobId,
  collaborators,
  ownerEmail,
  currentUserEmail,
  onAddCollaborators,
  onRemoveCollaborator,
}: ShareModalProps) {
  const { t } = useTranslation("sharing");
  const {
    memberEmails,
    isLoading: isLoadingMembers,
    error: membersError,
  } = useTenantMembers();
  const isMembersReady = !isLoadingMembers && !membersError;
  const [chips, setChips] = useState<EmailChip[]>([]);
  const [isInviting, setIsInviting] = useState(false);
  const [pendingRemoval, setPendingRemoval] = useState<Collaborator | null>(
    null,
  );
  const [isRemoving, setIsRemoving] = useState(false);

  const canRemove = currentUserEmail.toLowerCase() === ownerEmail.toLowerCase();

  const validChips = chips.filter((c) => c.status === "valid");
  const invalidChips = chips.filter((c) => c.status === "invalid");

  const existingEmails = collaborators.map((c) => c.email);

  const shareLink = `${window.location.origin}/jobs/${jobId}/overview`;

  const handleInvite = useCallback(async () => {
    if (validChips.length === 0 || isInviting) return;

    setIsInviting(true);
    try {
      await onAddCollaborators(validChips.map((c) => c.email));

      const parts: string[] = [];
      if (validChips.length > 0) {
        parts.push(t("toast.invited", { count: validChips.length }));
      }
      if (invalidChips.length > 0) {
        parts.push(t("toast.invalidSkipped", { count: invalidChips.length }));
      }
      toast.success(parts.join(" "));

      setChips([]);
      onOpenChange(false);
    } catch {
      // Hook already logged the error; surface failure to the user and keep
      // the modal open with chips intact so they can retry.
      toast.error(t("toast.inviteFailed"));
    } finally {
      setIsInviting(false);
    }
  }, [
    validChips,
    invalidChips,
    isInviting,
    onAddCollaborators,
    onOpenChange,
    t,
  ]);

  const handleConfirmRemove = useCallback(async () => {
    if (!pendingRemoval || isRemoving) return;
    const email = pendingRemoval.email;
    setIsRemoving(true);
    try {
      await onRemoveCollaborator(email);
      toast.success(t("toast.collaboratorRemoved", { email }));
      setPendingRemoval(null);
    } catch {
      // Hook already logged the error; surface failure to the user and keep
      // the confirmation open so they can retry.
      toast.error(t("toast.removeFailed"));
    } finally {
      setIsRemoving(false);
    }
  }, [pendingRemoval, isRemoving, onRemoveCollaborator, t]);

  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      toast.success(t("toast.linkCopied"));
    } catch {
      toast.error(t("toast.linkCopyFailed"));
    }
  }, [shareLink, t]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        setChips([]);
        setPendingRemoval(null);
      }
      onOpenChange(nextOpen);
    },
    [onOpenChange],
  );

  const isConfirmingRemoval = pendingRemoval !== null;

  return (
    <>
      <Dialog
        open={open && !isConfirmingRemoval}
        onOpenChange={handleOpenChange}
      >
        <DialogContent className="py-5 px-6 w-[520px] max-w-[90vw]">
          <DialogTitle className="text-left text-lg font-semibold text-foreground">
            {t("modal.title")} &ldquo;{title}&rdquo;
          </DialogTitle>

          <div className="flex flex-col gap-2">
            <Label className="text-xs font-medium text-secondary-600">
              {t("modal.emailLabel")}
            </Label>
            <div className="flex items-start gap-2">
              <div className="flex-1 min-w-0">
                {isLoadingMembers && (
                  <div
                    data-testid="members-loading-row"
                    className="flex items-center gap-2 rounded-xl border border-secondary-300 bg-secondary-50 px-3 py-2 text-sm text-secondary-500 min-h-[2.5rem]"
                  >
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                    <span>{t("modal.loadingMembers")}</span>
                  </div>
                )}
                {!isLoadingMembers && membersError && (
                  <div
                    data-testid="members-error-row"
                    role="alert"
                    className="flex items-center gap-2 rounded-xl border border-destructive/30 bg-rose-50 px-3 py-2 text-sm text-destructive min-h-[2.5rem]"
                  >
                    <AlertCircle className="h-4 w-4 shrink-0" aria-hidden />
                    <span>{t("modal.errorLoadingMembers")}</span>
                  </div>
                )}
                {isMembersReady && (
                  <EmailChipInput
                    chips={chips}
                    onChipsChange={setChips}
                    ownerEmail={ownerEmail}
                    existingEmails={existingEmails}
                    memberEmails={memberEmails}
                  />
                )}
              </div>
              <Button
                className="rounded-xl shrink-0"
                disabled={
                  !isMembersReady || validChips.length === 0 || isInviting
                }
                onClick={handleInvite}
              >
                {isInviting && (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                )}
                {t("modal.invite")}
              </Button>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Label className="text-xs font-medium text-secondary-600">
              {t("modal.membersLabel")}
            </Label>
            <div className="max-h-[15rem] overflow-y-auto -mx-2 px-2">
              <CollaboratorList
                collaborators={collaborators}
                ownerEmail={ownerEmail}
                currentUserEmail={currentUserEmail}
                onRequestRemove={setPendingRemoval}
                canRemove={canRemove}
              />
            </div>
          </div>

          <div className="flex flex-col gap-2 pt-1">
            <Label className="text-xs font-medium text-secondary-600">
              {t("modal.copyLinkLabel")}
            </Label>
            <div className="flex items-center gap-2">
              <Input
                readOnly
                value={shareLink}
                onFocus={(e) => e.currentTarget.select()}
                className="flex-1 text-sm text-secondary-600 bg-secondary-50/50 cursor-text"
                testId="share-link-input"
              />
              <Button
                variant="outline"
                className="rounded-xl shrink-0"
                onClick={handleCopyLink}
              >
                {t("modal.copyLink")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <RemoveCollaboratorConfirmation
        open={isConfirmingRemoval}
        onOpenChange={(nextOpen) => {
          if (!nextOpen && !isRemoving) setPendingRemoval(null);
        }}
        onConfirm={handleConfirmRemove}
        email={pendingRemoval?.email ?? ""}
        isRemoving={isRemoving}
      />
    </>
  );
}
